// Iter 88q + 88r — Global Email Kill Switch banner with QA allowlist.
// Iter 101 — Restyled to light orange (was rose-red). Only truly-destructive
// error UI should be red platform-wide; a PAUSED-but-recoverable state is a
// warning. Also made collapsible so it only shows its title when compact.
//
// Renders at the top of the Dashboard. Ops can pause / resume every
// outbound email (welcome invites, password resets, PS-manager
// assignments, commissions summaries, everything routed through Resend)
// with a single click.
//
// Iter 88r sparkle: super-admins can add QA inboxes to an allowlist
// while the kill switch is paused. Sends whose recipients are ALL in
// the allowlist get delivered normally — everything else still drops.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Mail, MailX, Loader2, Info, Plus, X, Check, ShieldCheck,
  ChevronDown, ChevronUp, Inbox, BellRing,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmailOutboxPanel, ResumeEmailDialog } from "@/components/EmailOutbox";

// Iter 271 — the switcher itself now lives in Settings → Platform → Platform.
// The Dashboard renders `variant="banner"`: a read-only, dismissable status
// strip that deep-links to the control. Dismissal is remembered per state
// signature, so flipping pause/resume brings the notice back.
const DISMISS_KEY = "email_kill_switch_dismissed_v1";

export function EmailKillSwitch({ isSuperAdmin, currentUserEmail, variant = "settings" }) {
  const [state,   setState]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [showAllowUi, setShowAllowUi] = useState(false);
  const [newEmail,    setNewEmail]    = useState("");
  const [savingAllow, setSavingAllow] = useState(false);
  // Aging reminder recipients — who gets the daily "held >24h" nudge.
  const [showReminderUi, setShowReminderUi] = useState(false);
  // Iter 101 — Collapsible container. Starts collapsed so the banner
  // just shows its status title; operator opens it to see details or
  // manage the allowlist. LIVE state stays collapsed too (single-line
  // green pill acts as a compact heartbeat).
  const [expanded, setExpanded] = useState(variant === "settings");
  const [dismissed, setDismissed] = useState("");
  const [showOutbox, setShowOutbox] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/emails-paused");
      setState(data);
    } catch { /* silent — banner just won't render */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (variant !== "banner") return;
    try { setDismissed(localStorage.getItem(DISMISS_KEY) || ""); } catch { /* ignore */ }
  }, [variant]);

  const dismiss = (sig) => {
    try { localStorage.setItem(DISMISS_KEY, sig); } catch { /* ignore */ }
    setDismissed(sig);
  };

  // Iter 292 — resuming asks what to do with the held outbox (send / keep / remove).
  const resume = async (heldAction) => {
    setBusy(true);
    const t = toast.loading("Resuming email delivery…");
    try {
      const { data } = await api.put("/settings/emails-paused", { paused: false, reason: "", held_action: heldAction });
      setState({ ...state, ...data, reason: "", paused: false });
      setResumeOpen(false);
      const r = data.held_result;
      const tail = heldAction === "send" && r ? ` ${r.sent} held email${r.sent === 1 ? "" : "s"} sent${r.failed ? `, ${r.failed} failed (still held)` : ""}.`
        : heldAction === "discard" && r ? ` ${r.discarded} held email${r.discarded === 1 ? "" : "s"} removed.`
        : data.held_count ? ` ${data.held_count} held email${data.held_count === 1 ? "" : "s"} waiting in the outbox.` : "";
      toast.success(`Emails resumed. Outbound delivery is live.${tail}`, { id: t, duration: 6000 });
      if (heldAction === "keep" && data.held_count) setShowOutbox(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update", { id: t });
    } finally { setBusy(false); }
  };

  const toggle = async (paused) => {
    if (!isSuperAdmin) return;
    if (!paused) { setResumeOpen(true); return; }
    const reason = window.prompt(
      "Optional: reason for pausing (audit log will record this):",
      state?.reason || "Config and testing — no external communication yet"
    ) || "";
    setBusy(true);
    const t = toast.loading("Pausing all emails…");
    try {
      const { data } = await api.put("/settings/emails-paused", { paused, reason });
      setState({ ...state, ...data, reason, paused });
      toast.warning("Emails paused — nothing leaves the platform; every send is kept in the held outbox.", { id: t, duration: 5000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update", { id: t });
    } finally { setBusy(false); }
  };

  const saveAllowlist = async (next) => {
    if (!isSuperAdmin) return;
    setSavingAllow(true);
    try {
      const { data } = await api.put("/settings/emails-paused",
        { allowed_recipients: next });
      setState({ ...state, ...data });
      toast.success(`Allowlist saved · ${next.length} address${next.length === 1 ? "" : "es"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingAllow(false); }
  };
  const addEmail = () => {
    const clean = (newEmail || "").trim().toLowerCase();
    if (!clean) return;
    // Iter 88s — accept either a full email OR a whole-domain (`@foo.io`).
    const isDomain = clean.startsWith("@") && clean.includes(".") && clean.length >= 4;
    const isEmail  = !clean.startsWith("@") && clean.includes("@") && !clean.endsWith("@");
    if (!isDomain && !isEmail) {
      toast.error("Enter a full email address or a whole domain like @unitedcloud.ca");
      return;
    }
    const cur = state?.allowed_recipients || [];
    if (cur.includes(clean)) { toast.info("Already on the allowlist"); return; }
    const next = [...cur, clean];
    setNewEmail("");
    saveAllowlist(next);
  };
  const removeEmail = (email) => {
    const next = (state?.allowed_recipients || []).filter((e) => e !== email);
    saveAllowlist(next);
  };
  const addMyself = () => {
    const clean = (currentUserEmail || "").trim().toLowerCase();
    if (!clean) return;
    const cur = state?.allowed_recipients || [];
    if (cur.includes(clean)) { toast.info("Your inbox is already on the allowlist"); return; }
    saveAllowlist([...cur, clean]);
  };

  if (loading || !state) return null;

  const paused = state.paused;
  const isBanner = variant === "banner";
  const signature = `${paused ? "paused" : "live"}|${state.updated_at || ""}`;
  if (isBanner && dismissed === signature) return null;
  const allowlist = state.allowed_recipients || [];
  const reminderList = state.reminder_recipients || [];
  const heldCount = state.held_count || 0;
  const myselfOnList = currentUserEmail && allowlist.includes(currentUserEmail.toLowerCase());

  // Iter 101 — orange = warning (recoverable), emerald = healthy (live).
  // No red anywhere except the destructive "Pause all email" button when
  // outbound is currently live (that click is a destructive action).
  const shell = paused
    ? "border-orange-300 bg-orange-50 text-orange-900"
    : "border-emerald-300 bg-emerald-50 text-emerald-900";

  return (
    <div className={`mb-4 rounded-lg border p-3 ${shell}`} data-testid="email-kill-switch">
      {/* Iter 101 — Collapsed header row. Always visible. Clicking the
          title / chevron toggles the details. */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="email-kill-switch-toggle"
          aria-expanded={expanded}
          className="flex flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 rounded">
          {paused ? <MailX className="h-5 w-5 shrink-0" /> : <Mail className="h-5 w-5 shrink-0" />}
          <div className="text-sm font-semibold flex flex-wrap items-center gap-2" data-testid="email-kill-switch-status">
            {paused ? (
              <><AlertTriangle className="h-4 w-4" /> Outbound email is PAUSED</>
            ) : (
              <>Outbound email is LIVE</>
            )}
            {paused && allowlist.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-semibold text-orange-800"
                    data-testid="email-allowlist-count">
                <ShieldCheck className="h-3 w-3" /> {allowlist.length} QA inbox{allowlist.length === 1 ? "" : "es"} allowed
              </span>
            )}
            {heldCount > 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${paused ? "bg-white/60 text-orange-800" : "bg-amber-100 text-amber-900"}`}
                    data-testid="email-held-count">
                <Inbox className="h-3 w-3" /> {heldCount} email{heldCount === 1 ? "" : "s"} held
              </span>
            )}
          </div>
          <span className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded-full opacity-70 hover:opacity-100" aria-hidden="true">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
        {isBanner && (
          <button type="button" onClick={() => dismiss(signature)}
                  title="Dismiss this notice"
                  aria-label="Dismiss"
                  data-testid="email-kill-switch-dismiss"
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Iter 101 — Expanded body. Everything below is hidden until the
          operator opens the banner. */}
      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1 text-xs opacity-80">
              {paused ? (
                <>All Resend traffic is held at the wire &mdash; except recipients on the QA allowlist below. Held emails wait in the outbox to be sent or removed. Safe for config &amp; testing.</>
              ) : (
                <>Every send goes to real recipients. Turn this off if you spot anything erroneous.</>
              )}
              {state.updated_by_name && (
                <> <span className="opacity-70">&middot; last change by <b>{state.updated_by_name}</b>{state.updated_at ? ` at ${new Date(state.updated_at).toLocaleString()}` : ""}</span></>
              )}
              {state.reason && paused && (
                <> <span className="italic opacity-70">&middot; reason: {state.reason}</span></>
              )}
            </div>
            {isBanner ? (
              <a href="/settings?tab=platform"
                 data-testid="email-kill-switch-settings-link"
                 className="text-xs font-semibold underline underline-offset-2 opacity-80 hover:opacity-100">
                {heldCount > 0 ? `Open the held outbox (${heldCount}) →` : "Manage in Settings → Platform →"}
              </a>
            ) : (
              <>
                {isSuperAdmin && paused && (
                  <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => setShowAllowUi((v) => !v)}
                          data-testid="email-kill-switch-manage-allowlist"
                          className="border-orange-300 bg-white text-orange-800 hover:bg-orange-100">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {showAllowUi ? "Hide QA allowlist" : "QA allowlist"}
                  </Button>
                )}
                <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => setShowOutbox((v) => !v)}
                        data-testid="email-kill-switch-outbox"
                        className={`bg-white ${paused ? "border-orange-300 text-orange-800 hover:bg-orange-100" : "border-emerald-300 text-emerald-800 hover:bg-emerald-100"}`}>
                  <Inbox className="h-3.5 w-3.5" />
                  {showOutbox ? "Hide outbox" : `Held outbox${heldCount ? ` (${heldCount})` : ""}`}
                </Button>
                {isSuperAdmin && (
                  <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => setShowReminderUi((v) => !v)}
                          data-testid="email-kill-switch-manage-reminders"
                          className={`bg-white ${paused ? "border-orange-300 text-orange-800 hover:bg-orange-100" : "border-emerald-300 text-emerald-800 hover:bg-emerald-100"}`}>
                    <BellRing className="h-3.5 w-3.5" />
                    {showReminderUi ? "Hide reminders" : `Aging reminders${reminderList.length ? ` (${reminderList.length})` : ""}`}
                  </Button>
                )}
              </>
            )}
            {isBanner ? null : isSuperAdmin ? (
              paused ? (
                <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => toggle(false)}
                        data-testid="email-kill-switch-resume"
                        className="border-emerald-400 bg-white text-emerald-700 hover:bg-emerald-100">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Resume email
                </Button>
              ) : (
                // Iter 101 — Pause is destructive so this button stays red.
                <Button size="sm" disabled={busy}
                        onClick={() => toggle(true)}
                        data-testid="email-kill-switch-pause"
                        className="bg-red-600 hover:bg-red-700 text-white">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailX className="h-3.5 w-3.5" />}
                  Pause all email
                </Button>
              )
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] opacity-70">
                <Info className="h-3 w-3" /> Super-admin toggle
              </span>
            )}
          </div>

          {!isBanner && showOutbox && (
            <EmailOutboxPanel paused={paused} canEdit={isSuperAdmin} onChanged={load} />
          )}

          {!isBanner && isSuperAdmin && showReminderUi && (
            <div className="rounded-md border border-amber-200 bg-white/70 p-3 space-y-2"
                 data-testid="email-reminder-panel">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800 flex items-center gap-1.5">
                <BellRing className="h-3.5 w-3.5" /> Aging reminders &mdash; who's alerted when a held email waits over 24h
              </div>
              <p className="text-xs text-amber-900/80 leading-relaxed">
                Once a day, as long as any email has sat in the held outbox for more than 24 hours, these addresses
                get a summary so nothing is forgotten. The reminder always sends, even while outbound email is paused.
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="email-reminder-chips">
                {reminderList.length === 0 && (
                  <span className="text-xs italic text-amber-800/70">Nobody chosen &mdash; no reminder is sent.</span>
                )}
                {reminderList.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-white px-2 py-0.5 text-xs text-amber-900"
                        data-testid={`email-reminder-chip-${email}`}>
                    <Check className="h-3 w-3 text-emerald-600" /> {email}
                  </span>
                ))}
              </div>
              <a href="/settings?tab=notifications" className="inline-block text-xs font-semibold text-amber-800 underline underline-offset-2"
                 data-testid="email-reminder-settings-link">Choose who gets it in Settings &rarr; Notifications &rarr; Admin alert recipients</a>
              {state.last_reminder?.at && (
                <div className="text-[11px] text-amber-800/70" data-testid="email-reminder-last">
                  Last reminder: {new Date(state.last_reminder.at).toLocaleString()} · {state.last_reminder.status}
                  {typeof state.last_reminder.aged === "number" ? ` · ${state.last_reminder.aged} aged` : ""}
                </div>
              )}
            </div>
          )}

          {!isBanner && isSuperAdmin && paused && showAllowUi && (
            <div className="rounded-md border border-orange-200 bg-white/70 p-3 space-y-2"
                 data-testid="email-allowlist-panel">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-orange-800">
                QA allowlist &mdash; send-throughs while the kill switch is on
              </div>
              <p className="text-xs text-orange-900/80 leading-relaxed">
                Any send where every recipient is on this list will be delivered
                normally so you can preview real template output in a real inbox.
                Add a full email (<code className="mx-0.5 rounded bg-orange-100 px-1">keith@unitedcloud.ca</code>)
                or a whole domain (<code className="mx-0.5 rounded bg-orange-100 px-1">@unitedcloud.ca</code>)
                to auto-pass every staff address. Anyone <em>not</em> on the list
                still gets dropped &mdash; even if they&rsquo;re one of many recipients
                on the same message.
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="email-allowlist-chips">
                {allowlist.length === 0 && (
                  <span className="text-xs italic text-orange-800/70">
                    Nothing allowlisted &mdash; every send is being dropped.
                  </span>
                )}
                {allowlist.map((email) => (
                  <span key={email}
                        className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-white px-2 py-0.5 text-xs text-orange-900"
                        data-testid={`email-allowlist-chip-${email}`}>
                    <Check className="h-3 w-3 text-emerald-600" /> {email}
                    <button className="ml-0.5 text-orange-500 hover:text-orange-700"
                            title={`Remove ${email}`}
                            onClick={() => removeEmail(email)}
                            disabled={savingAllow}
                            data-testid={`email-allowlist-remove-${email}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Input value={newEmail}
                       onChange={(e) => setNewEmail(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                       placeholder="qa-inbox@yourdomain.io"
                       className="h-8 w-64 text-xs"
                       disabled={savingAllow}
                       data-testid="email-allowlist-input" />
                <Button size="sm" variant="outline" onClick={addEmail}
                        disabled={savingAllow || !newEmail}
                        data-testid="email-allowlist-add"
                        className="h-8 border-orange-300 text-orange-800 hover:bg-orange-100">
                  {savingAllow ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                               : <Plus className="h-3.5 w-3.5" />}
                  Add
                </Button>
                {currentUserEmail && !myselfOnList && (
                  <Button size="sm" variant="ghost" onClick={addMyself}
                          disabled={savingAllow}
                          data-testid="email-allowlist-add-me"
                          className="h-8 text-orange-800 hover:bg-orange-100">
                    <ShieldCheck className="h-3.5 w-3.5" /> Add my inbox
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <ResumeEmailDialog open={resumeOpen} heldCount={heldCount} busy={busy} onCancel={() => setResumeOpen(false)} onConfirm={resume} />
    </div>
  );
}
